import { useEffect, useState, useMemo } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { API_BASE } from '../../lib/api-base';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Navigate } from 'react-router-dom';
import { SlidersHorizontal, RotateCcw, Save, AlertCircle, CheckCircle2, FlaskConical } from 'lucide-react';
import { loadLiveRules } from '../../lib/sizing-profile-shared';

const fmt = (n) => Number(n).toFixed(3).replace(/\.?0+$/, '');

function NumberCell({ value, onChange, defaultValue, step = 0.05, testId, min = 0, max = 5 }) {
  const changed = Math.abs(Number(value) - Number(defaultValue)) > 0.0001;
  return (
    <input
      type="number" step={step} min={min} max={max}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className={`w-24 px-2 py-1 text-sm rounded border focus:outline-none focus:ring-1 focus:ring-[#9ec043] tabular-nums text-right ${
        changed
          ? 'border-amber-400 bg-amber-50 text-amber-900 font-semibold'
          : 'border-gray-200 bg-white text-gray-700'
      }`}
      data-testid={testId}
    />
  );
}

export default function SizingRulesPage({ embedded = false } = {}) {
  const { user } = useAuth();
  const { can, ready: permReady } = usePermissions();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);  // {kind, msg}
  const [sandbox, setSandbox] = useState({
    text: '10 kWp Einfamilienhaus mit Wärmepumpe und Notstrom',
    pvKwp: 10,
    annualKwh: '',
    result: null,
    loading: false,
    error: null,
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await authFetch('/api/sizing-rules');
      if (!r.ok) throw new Error('Laden fehlgeschlagen');
      const d = await r.json();
      setData(d);
      setDraft(JSON.parse(JSON.stringify(d.active)));
    } catch (e) {
      setFeedback({ kind: 'error', msg: e.message || 'Fehler' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const isDirty = useMemo(() => {
    if (!data || !draft) return false;
    return JSON.stringify(draft) !== JSON.stringify(data.active);
  }, [data, draft]);

  if (!user) return null;
  // 04.02.2026: Matrix-basiertes Gating statt `user.role !== 'admin'`.
  // Wer `sizing.admin` in der Aktionsmatrix hat, kommt rein — egal ob
  // Admin, Vertrieb oder eine andere Rolle. Warten bis Permissions geladen
  // sind, damit wir Non-Admins nicht fälschlich wegschicken.
  if (!embedded && permReady && !can('sizing.admin'))
    return <Navigate to="/dashboard" replace />;

  if (loading || !data || !draft) {
    return <div className={embedded ? "p-2 text-sm text-gray-500" : "p-6 text-sm text-gray-500"}>Lädt …</div>;
  }

  const labels = data.labels;
  const defaultsBF = Object.fromEntries(
    data.defaults.baseFactors.map(e => [`${e.profileClass}|${e.workTime || 'none'}`, e.value])
  );

  const updateBaseFactor = (idx, value) => {
    const next = [...draft.baseFactors];
    next[idx] = { ...next[idx], value };
    setDraft({ ...draft, baseFactors: next });
  };

  const updateUplift = (key, value) => {
    setDraft({ ...draft, uplifts: { ...draft.uplifts, [key]: value } });
  };

  const updateLimit = (key, idx, value) => {
    const pair = [...(draft.limits[key] || [0, 0])];
    pair[idx] = value;
    setDraft({ ...draft, limits: { ...draft.limits, [key]: pair } });
  };

  const handleSave = async () => {
    setSaving(true); setFeedback(null);
    try {
      const r = await authFetch('/api/sizing-rules', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Speichern fehlgeschlagen');
      setFeedback({ kind: 'ok', msg: 'Änderungen gespeichert — Frag HANS und Konfigurator nutzen die neuen Werte ab sofort.' });
      // Frontend-Tabellen ebenfalls live aktualisieren
      await loadLiveRules(API_BASE);
      await refresh();
    } catch (e) {
      setFeedback({ kind: 'error', msg: String(e.message || e) });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Alle Faktoren auf die Werks-Defaults zurücksetzen?')) return;
    setSaving(true); setFeedback(null);
    try {
      const r = await authFetch('/api/sizing-rules/reset', { method: 'POST' });
      if (!r.ok) throw new Error('Reset fehlgeschlagen');
      setFeedback({ kind: 'ok', msg: 'Werks-Defaults wiederhergestellt.' });
      await loadLiveRules(API_BASE);
      await refresh();
    } catch (e) {
      setFeedback({ kind: 'error', msg: String(e.message || e) });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraft(JSON.parse(JSON.stringify(data.active)));
    setFeedback(null);
  };

  const runSandbox = async () => {
    setSandbox(s => ({ ...s, loading: true, error: null }));
    try {
      const r = await authFetch('/api/sizing-rules/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: sandbox.text || '',
          pvKwp: Number(sandbox.pvKwp) || null,
          annualKwh: sandbox.annualKwh ? Number(sandbox.annualKwh) : null,
          // Aktueller Draft (ungespeichert) — Backend wendet ihn temporär an
          draft: isDirty ? draft : null,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Berechnung fehlgeschlagen');
      setSandbox(s => ({ ...s, result: j, loading: false }));
    } catch (e) {
      setSandbox(s => ({ ...s, error: String(e.message || e), loading: false }));
    }
  };

  return (
    <div className={embedded ? "" : "p-6 max-w-6xl mx-auto"} data-testid="sizing-rules-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#32373c] flex items-center gap-2">
            <SlidersHorizontal className="w-6 h-6 text-[#9ec043]" />
            Auslegungs-Regelwerk
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Hier passt du die <strong>Basisfaktoren (kWh/kWp)</strong>, <strong>Uplifts</strong> und
            <strong> Min/Max-Grenzen</strong> an, mit denen Frag HANS, der Konfigurator und der Anfrage-
            Auswertungs-Workflow die Speichergröße deterministisch berechnen. Änderungen wirken
            <strong> sofort</strong> in allen Modulen — kein Code-Update erforderlich.
          </p>
          {data.meta?.isOverridden && (
            <p className="text-[11px] text-amber-700 mt-2">
              Aktuell überschrieben — zuletzt bearbeitet von <strong>{data.meta.updatedBy}</strong>{' '}
              am {data.meta.updatedAt ? new Date(data.meta.updatedAt).toLocaleString('de-DE') : '—'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleReset} disabled={saving}
                  className="text-xs px-3 py-1.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1.5"
                  data-testid="rules-reset">
            <RotateCcw className="w-3.5 h-3.5" /> Werks-Defaults
          </button>
          <button onClick={handleDiscard} disabled={!isDirty || saving}
                  className="text-xs px-3 py-1.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  data-testid="rules-discard">
            Verwerfen
          </button>
          <button onClick={handleSave} disabled={!isDirty || saving}
                  className="text-xs px-3 py-1.5 rounded bg-[#9ec043] text-white font-semibold hover:bg-[#8ab035] disabled:opacity-40 inline-flex items-center gap-1.5"
                  data-testid="rules-save">
            <Save className="w-3.5 h-3.5" /> Speichern
          </button>
        </div>
      </div>

      {feedback && (
        <div className={`mb-4 px-3 py-2 rounded text-sm flex items-start gap-2 ${
          feedback.kind === 'ok'
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
            : 'bg-rose-50 border border-rose-200 text-rose-900'
        }`} data-testid="rules-feedback">
          {feedback.kind === 'ok'
            ? <CheckCircle2 className="w-4 h-4 mt-0.5" />
            : <AlertCircle className="w-4 h-4 mt-0.5" />}
          <span>{feedback.msg}</span>
        </div>
      )}

      {/* Sandbox: ungespeicherte Änderungen live testen */}
      <section className="bg-sky-50/50 border border-sky-200 rounded-lg p-4 mb-5"
               data-testid="rules-sandbox">
        <h2 className="text-sm font-semibold text-sky-900 mb-1 flex items-center gap-2">
          <FlaskConical className="w-4 h-4" /> Sandbox — Änderungen testen
        </h2>
        <p className="text-[11px] text-sky-800/80 mb-3">
          Beispielanfrage eintippen → das System wendet den <strong>aktuellen Draft</strong>{' '}
          {isDirty
            ? <span className="font-semibold">(inklusive ungespeicherter Werte)</span>
            : '(noch keine Änderungen)'} an
          und zeigt Profilklasse, alle gegriffenen Uplifts, den finalen Faktor und die nutzbare
          Kapazität — ohne dass dein Live-Regelwerk verändert wird.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-3">
          <div className="md:col-span-6">
            <div className="text-[10px] uppercase tracking-wide text-sky-700 font-semibold mb-1">Beispieltext</div>
            <input
              type="text" value={sandbox.text}
              onChange={(e) => setSandbox(s => ({ ...s, text: e.target.value }))}
              placeholder='z.B. "10 kWp Einfamilienhaus mit Wärmepumpe und Notstrom"'
              className="w-full px-3 py-2 text-sm rounded border border-sky-200 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
              data-testid="sandbox-text" />
          </div>
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-wide text-sky-700 font-semibold mb-1">PV-Leistung</div>
            <div className="relative">
              <input
                type="number" step="0.5" min="0" max="500"
                value={sandbox.pvKwp}
                onChange={(e) => setSandbox(s => ({ ...s, pvKwp: e.target.value }))}
                placeholder="kWp"
                className="w-full px-3 py-2 pr-10 text-sm rounded border border-sky-200 bg-white text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-sky-400"
                data-testid="sandbox-pvkwp" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 pointer-events-none">kWp</span>
            </div>
          </div>
          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-wide text-sky-700 font-semibold mb-1">Jahresverbrauch (optional)</div>
            <div className="relative">
              <input
                type="number" step="500" min="0" max="500000"
                value={sandbox.annualKwh}
                onChange={(e) => setSandbox(s => ({ ...s, annualKwh: e.target.value }))}
                placeholder="kWh/Jahr"
                className="w-full px-3 py-2 pr-14 text-sm rounded border border-sky-200 bg-white text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-sky-400"
                data-testid="sandbox-annual" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 pointer-events-none">kWh/J.</span>
            </div>
          </div>
          <div className="md:col-span-1 flex items-end">
            <button onClick={runSandbox} disabled={sandbox.loading || !sandbox.pvKwp}
                    className="w-full px-3 py-2 text-xs font-semibold rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40"
                    data-testid="sandbox-run">
              {sandbox.loading ? '…' : 'Testen'}
            </button>
          </div>
        </div>

        {sandbox.error && (
          <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1">
            {sandbox.error}
          </div>
        )}

        {sandbox.result && !sandbox.error && (
          <div className="bg-white border border-sky-100 rounded-md p-3 text-[12px] leading-relaxed"
               data-testid="sandbox-result">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">Profilklasse</div>
                <div className="font-semibold text-gray-700">{sandbox.result.profileLabel}</div>
                {sandbox.result.workTimeLabel && (
                  <div className="text-[10px] text-gray-500">{sandbox.result.workTimeLabel}</div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">Basis-Faktor</div>
                <div className="font-semibold text-gray-700 tabular-nums">{sandbox.result.baseFactor.toFixed(3)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">Final (geklammert)</div>
                <div className="font-semibold text-[#7ca735] tabular-nums">
                  {sandbox.result.finalFactor.toFixed(3)}
                  <span className="text-[10px] text-gray-400 ml-1">
                    ({sandbox.result.limits.min.toFixed(2)}–{sandbox.result.limits.max.toFixed(2)})
                  </span>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">Nutzbar</div>
                <div className="font-semibold text-[#7ca735] tabular-nums">
                  {sandbox.result.usableKwh.toFixed(2)} kWh
                </div>
              </div>
            </div>

            {Object.keys(sandbox.result.appliedUplifts || {}).length > 0 && (
              <div className="mb-2">
                <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Aktive Uplifts</div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(sandbox.result.appliedUplifts).map(([k, v]) => (
                    <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-900 rounded text-[10px] font-medium">
                      {labels.uplifts[k] || k}
                      <span className="tabular-nums">+{Number(v).toFixed(2)}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="text-[10px] text-gray-500 mb-1">Systemfamilie (§21)</div>
            <div className="text-[12px] text-gray-700 mb-2">→ {sandbox.result.systemTendency}</div>
            <div className="text-[10px] text-gray-400 italic mb-2">
              Hinweis: Konkretes Modell, Modul-Anzahl, Notstrom-Klassifizierung und PV-Kopplung
              werden im Konfigurator entschieden — die Sandbox prüft ausschließlich das Regelwerk
              (Profilklasse, Faktoren, Limits).
            </div>

            <details className="text-[11px] text-gray-500">
              <summary className="cursor-pointer hover:text-gray-700">Berechnung & Annahmen</summary>
              <ul className="mt-1 ml-3 list-disc space-y-0.5">
                {(sandbox.result.assumptions || []).map((a, i) => <li key={i}>{a}</li>)}
              </ul>
              {(sandbox.result.missingInfo || []).length > 0 && (
                <>
                  <div className="mt-2 text-gray-400">Fehlende Angaben für eine finale Auslegung:</div>
                  <ul className="ml-3 list-disc space-y-0.5">
                    {sandbox.result.missingInfo.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </>
              )}
            </details>
          </div>
        )}
      </section>

      {/* §6 BASIS-FAKTOREN */}
      <section className="bg-white border border-gray-200 rounded-lg p-4 mb-5" data-testid="rules-basefactors">
        <h2 className="text-sm font-semibold text-[#32373c] mb-1">§6 Basisfaktoren <span className="text-gray-400 font-normal">(kWh nutzbar pro kWp PV-Leistung)</span></h2>
        <p className="text-[11px] text-gray-500 mb-3">
          Wert pro Profilklasse × Arbeitszeit. Bei <em>Privat</em> ist die Arbeitszeit irrelevant
          (Standard / alle Zeiten). Bei Gewerbe wird der spezifischere Eintrag bevorzugt; falls
          keiner passt, fällt das System auf den <em>Standard</em>-Eintrag der Klasse zurück.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-gray-100 text-gray-400 text-[10px] uppercase tracking-wide">
                <th className="text-left font-semibold py-1.5">Profilklasse</th>
                <th className="text-left font-semibold py-1.5">Arbeitszeit</th>
                <th className="text-right font-semibold py-1.5 px-2">Werks-Default</th>
                <th className="text-right font-semibold py-1.5 px-2">Aktuell</th>
              </tr>
            </thead>
            <tbody>
              {draft.baseFactors.map((e, i) => {
                const k = `${e.profileClass}|${e.workTime || 'none'}`;
                const def = defaultsBF[k] ?? e.value;
                return (
                  <tr key={k} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-1.5 text-gray-700">
                      {labels.profileClasses[e.profileClass] || e.profileClass}
                    </td>
                    <td className="py-1.5 text-gray-500">
                      {labels.workTimes[e.workTime || 'none'] || (e.workTime || '—')}
                    </td>
                    <td className="py-1.5 text-right text-gray-400 px-2 tabular-nums">{fmt(def)}</td>
                    <td className="py-1.5 text-right px-2">
                      <NumberCell value={e.value} onChange={(v) => updateBaseFactor(i, v)}
                                  defaultValue={def} step={0.05}
                                  testId={`bf-${e.profileClass}-${e.workTime || 'none'}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* §7 UPLIFTS */}
      <section className="bg-white border border-gray-200 rounded-lg p-4 mb-5" data-testid="rules-uplifts">
        <h2 className="text-sm font-semibold text-[#32373c] mb-1">§7 Uplifts <span className="text-gray-400 font-normal">(additive Korrekturen pro erkanntem Sonderfall)</span></h2>
        <p className="text-[11px] text-gray-500 mb-3">
          Der Uplift wird auf den Basisfaktor <em>addiert</em>, wenn der jeweilige Auslöser im Anfragetext
          erkannt wird (z.B. „Notstrom", „Wärmepumpe", „Wallbox"). Anschließend werden die
          Min/Max-Grenzen aus §9 angewendet.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          {Object.entries(draft.uplifts).map(([k, v]) => {
            const def = data.defaults.uplifts[k] ?? v;
            return (
              <div key={k} className="flex items-center justify-between gap-3 py-1 border-b border-gray-50 last:border-0">
                <span className="text-[12px] text-gray-700">{labels.uplifts[k] || k}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-400 tabular-nums">Default {fmt(def)}</span>
                  <NumberCell value={v} onChange={(val) => updateUplift(k, val)}
                              defaultValue={def} step={0.05}
                              testId={`uplift-${k}`} min={-1} max={2} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* §9 LIMITS */}
      <section className="bg-white border border-gray-200 rounded-lg p-4 mb-5" data-testid="rules-limits">
        <h2 className="text-sm font-semibold text-[#32373c] mb-1">§9 Min/Max-Grenzen <span className="text-gray-400 font-normal">(harter Klemm-Bereich pro Profilklasse)</span></h2>
        <p className="text-[11px] text-gray-500 mb-3">
          Nach Basisfaktor + Uplifts wird der Wert auf [Min, Max] geklammert.
          <em> *_backup</em>-Varianten greifen, wenn Notstrom erkannt wurde;
          <em> *_extended</em>-Varianten bei verlängerten Arbeitszeiten.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-gray-100 text-gray-400 text-[10px] uppercase tracking-wide">
                <th className="text-left font-semibold py-1.5">Profilklasse</th>
                <th className="text-right font-semibold py-1.5 px-2">Default</th>
                <th className="text-right font-semibold py-1.5 px-2">Min</th>
                <th className="text-right font-semibold py-1.5 px-2">Max</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(draft.limits).map(([k, [mn, mx]]) => {
                const def = data.defaults.limits[k] || [mn, mx];
                return (
                  <tr key={k} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-1.5 text-gray-700">{labels.limits[k] || k}</td>
                    <td className="py-1.5 text-right text-gray-400 px-2 tabular-nums text-[10px]">
                      {fmt(def[0])}–{fmt(def[1])}
                    </td>
                    <td className="py-1.5 text-right px-2">
                      <NumberCell value={mn} onChange={(v) => updateLimit(k, 0, v)}
                                  defaultValue={def[0]} step={0.05}
                                  testId={`limit-${k}-min`} />
                    </td>
                    <td className="py-1.5 text-right px-2">
                      <NumberCell value={mx} onChange={(v) => updateLimit(k, 1, v)}
                                  defaultValue={def[1]} step={0.05}
                                  testId={`limit-${k}-max`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="text-[11px] text-gray-400 italic">
        Gespeicherte Änderungen wirken sofort in <strong>Frag HANS</strong>, im
        <strong> Anfragen-Auswertungs-Modul</strong> und im
        <strong> Konfigurator-What-If</strong>. Sie überschreiben die Werks-Defaults dauerhaft —
        nutze „Werks-Defaults", um auf den Auslieferungszustand zurückzukehren.
      </div>
    </div>
  );
}
